/* =============================================================
   Nine Two Five Silver — wishlist.js · v1 · 22 September 2026
   Hearts on cards and the product page, header count, the wishlist
   page, sharing. Guests keep the list in localStorage; signed-in
   customers are synced to a customer metafield through the app proxy
   at /apps/wishlist (docs/wishlist-plan.md). No dependencies.
   ============================================================= */
(function () {
  'use strict';

  var KEY = 'ntf:wishlist:v1';
  var SHARE_KEY = 'ntf:wishlist:share:v1';
  var MAX_ITEMS = 100;
  var URL_LINK_MAX = 40;
  var PROXY = '/apps/wishlist';
  var body = document.body;
  var PAGE_URL = body.getAttribute('data-wishlist-url') || '/pages/wishlist';
  var CUSTOMER_ID = body.getAttribute('data-customer-id') || '';
  // The shop's primary domain ({{ shop.url }}): share links use it even when this page is on a preview host,
  // where the recipient would have none of their own cookies or saved list (#309).
  var SHOP_URL = (body.getAttribute('data-shop-url') || '').replace(/\/+$/, '');

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };
  var nowIso = function () { return new Date().toISOString(); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };

  /* ------- Storage (localStorage, in-memory fallback) ------- */
  var mem = null;

  function cleanItem(raw) {
    if (!raw || typeof raw.handle !== 'string' || !/^[a-z0-9-]{1,255}$/.test(raw.handle)) return null;
    var vid = raw.variantId == null || raw.variantId === '' ? null : String(raw.variantId);
    if (vid && !/^\d{1,20}$/.test(vid)) vid = null;
    var added = typeof raw.addedAt === 'string' && !isNaN(Date.parse(raw.addedAt)) ? new Date(raw.addedAt).toISOString() : nowIso();
    return { handle: raw.handle, variantId: vid, addedAt: added };
  }

  // One entry per piece and size (#305): a ring saved in J and K is two entries. A null variant (a piece with
  // no sizes, or an entry saved before sizes counted) is its own key. Exact duplicates keep the earliest addedAt.
  function keyOf(handle, variantId) { return handle + ':' + (variantId || ''); }

  function dedupe(items) {
    var byKey = {}, out = [];
    items.forEach(function (it) {
      var k = keyOf(it.handle, it.variantId), ex = byKey[k];
      if (!ex) { byKey[k] = it; out.push(it); return; }
      if (it.addedAt < ex.addedAt) ex.addedAt = it.addedAt;
    });
    return out;
  }

  function cap(items) {
    items.sort(function (a, b) { return a.addedAt < b.addedAt ? -1 : a.addedAt > b.addedAt ? 1 : 0; });
    return items.length > MAX_ITEMS ? items.slice(items.length - MAX_ITEMS) : items;
  }

  function normalise(data) {
    var items = data && Array.isArray(data.items) ? data.items.map(cleanItem).filter(Boolean) : [];
    return { v: 1, items: cap(dedupe(items)), syncedAt: data && data.syncedAt ? data.syncedAt : null };
  }

  function read() {
    if (mem) return mem;
    var data = null;
    try { var raw = localStorage.getItem(KEY); data = raw ? JSON.parse(raw) : null; } catch (e) { data = null; }
    mem = normalise(data);
    return mem;
  }

  function write(state) {
    mem = state;
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode or blocked: memory only */ }
  }

  function getItems() { return read().items.slice(); }
  // variantId undefined: any entry for the piece; null or an id: that exact entry.
  function matches(it, handle, variantId) { return it.handle === handle && (variantId === undefined || (it.variantId || null) === (variantId ? String(variantId) : null)); }
  function find(handle, variantId) { var items = read().items; for (var i = 0; i < items.length; i++) if (matches(items[i], handle, variantId)) return items[i]; return null; }
  function has(handle, variantId) { return !!find(handle, variantId); }
  // The sizes saved for a piece, oldest first (entries with a variant only).
  function sizesOf(handle) { return read().items.filter(function (it) { return it.handle === handle && it.variantId; }); }

  function add(handle, variantId) {
    var state = read();
    if (has(handle, variantId || null)) return;
    var items = state.items.slice();
    items.push({ handle: handle, variantId: variantId ? String(variantId) : null, addedAt: nowIso() });
    state.items = cap(items);
    write(state);
    afterChange();
  }

  // Without a variantId, every entry for the piece goes (a heart on a piece with no sizes).
  function remove(handle, variantId) {
    var state = read();
    state.items = state.items.filter(function (it) { return !matches(it, handle, variantId); });
    write(state);
    afterChange();
  }

  function replaceAll(items, syncedAt) {
    var state = read();
    state.items = cap(dedupe(items.map(cleanItem).filter(Boolean)));
    if (syncedAt !== undefined) state.syncedAt = syncedAt;
    write(state);
  }

  function afterChange() {
    renderHearts();
    renderCount();
    scheduleSync(false);
    scheduleRepublish();
  }

  /* ------- Analytics (Shopify custom events; GA4 parameter names) ------- */
  function publish(name, payload) {
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, payload); } catch (e) { /* ignore */ }
  }

  function publishAdd(heart, handle, variantId) {
    var price = parseInt(heart && heart.getAttribute('data-price'), 10);
    var value = isNaN(price) ? undefined : price / 100;
    publish('ntf:add_to_wishlist', {
      currency: 'GBP',
      value: value,
      items: [{ item_id: handle, item_name: heart ? heart.getAttribute('data-title') || handle : handle, item_variant: variantId || undefined, price: value }]
    });
  }

  /* ------- Hearts and header count ------- */
  function pdpVariantId(heart) {
    if (!heart.hasAttribute('data-wishlist-heart-pdp')) return heart.getAttribute('data-variant-id') || null;
    if (heart.getAttribute('data-default-variant') === 'true') return null;
    // The gift voucher page (#305) picks its amount with radio tiles named "id".
    var input = $('form[data-product-form] input[name="id"]') || $('form[data-gift-voucher-form] input[name="id"]:checked');
    return input && input.value ? input.value : heart.getAttribute('data-variant-id') || null;
  }

  // Writes only what changed: the label text is a child-list change, and the MutationObserver
  // below would otherwise repaint on every frame for as long as the page is open.
  function setIfChanged(el, attr, value) { if (el.getAttribute(attr) !== value) el.setAttribute(attr, value); }
  function paintHeart(heart) {
    var handle = heart.getAttribute('data-handle');
    var title = heart.getAttribute('data-title') || 'this piece';
    var label = $('[data-wishlist-heart-label]', heart);
    var sized = heart.getAttribute('data-sized') === 'true';
    var on, aria, text;
    if (sized && heart.hasAttribute('data-wishlist-heart-pdp')) {
      // Product page (#305): the button names the chosen size and follows the size pills.
      var size = heartVariant(heart, pdpVariantId(heart));
      on = !!size && has(handle, size.id);
      if (!size) text = 'Choose ' + (sizeKind(heart) === 'voucher' ? 'an amount' : 'a size') + ' to save';
      else if (on) text = capitalise(sizeWords(heart, size.title).phrase) + ' saved · Remove';
      else text = 'Save ' + sizeWords(heart, size.title).phrase + ' to wishlist';
      aria = text.replace(' · ', ', ') + ': ' + title; // starts with the visible words, so voice control finds it
      if (heart.disabled !== !size) heart.disabled = !size;
    } else if (sized && !heart.getAttribute('data-variant-id')) {
      // Card heart on a piece with sizes: opens the size picker; filled when any size is saved.
      var saved = sizesOf(handle);
      on = has(handle);
      aria = on && saved.length
        ? title + ', saved in ' + listWords(saved.map(function (it) { var v = heartVariant(heart, it.variantId); return v ? sizeWords(heart, v.title).phrase : 'a size'; })) + '. Change saved sizes'
        : 'Save ' + title + ' to your wishlist: choose ' + (sizeKind(heart) === 'voucher' ? 'an amount' : 'a size');
      text = on ? 'Saved' : heart.getAttribute('data-label-off') || 'Save to wishlist';
      setIfChanged(heart, 'aria-haspopup', 'dialog');
    } else if (sized) {
      // A heart for one size (shared list cards).
      var fixed = heartVariant(heart, heart.getAttribute('data-variant-id'));
      var words = fixed ? sizeWords(heart, fixed.title).phrase + ' of ' : '';
      on = has(handle, heart.getAttribute('data-variant-id'));
      aria = (on ? 'Remove ' : 'Save ') + words + title + (on ? ' from your wishlist' : ' to your wishlist');
      text = on ? 'Saved' : heart.getAttribute('data-label-off') || 'Save to wishlist';
    } else {
      on = has(handle);
      aria = (on ? 'Remove ' : 'Save ') + title + (on ? ' from your wishlist' : ' to your wishlist');
      text = on ? 'Saved' : heart.getAttribute('data-label-off') || 'Save to wishlist';
    }
    setIfChanged(heart, 'aria-pressed', on ? 'true' : 'false');
    setIfChanged(heart, 'aria-label', aria);
    if (label && label.textContent !== text) label.textContent = text;
  }

  function renderHearts(root) {
    $$('[data-wishlist-heart]', root || document).forEach(paintHeart);
  }

  function renderCount() {
    var n = read().items.length;
    $$('[data-wishlist-toggle]').forEach(function (el) {
      el.setAttribute('data-count', n);
      el.setAttribute('aria-label', 'Wishlist, ' + n + ' saved');
      var c = $('.wishlist-toggle__count', el);
      if (c) c.textContent = n;
    });
  }

  function pop(heart) {
    heart.classList.remove('is-popping');
    void heart.offsetWidth;
    heart.classList.add('is-popping');
    var done = function () { heart.classList.remove('is-popping'); heart.removeEventListener('animationend', done); };
    heart.addEventListener('animationend', done);
  }

  function bindHearts() {
    document.addEventListener('click', function (e) {
      var heart = e.target.closest && e.target.closest('[data-wishlist-heart]');
      if (!heart) return;
      e.preventDefault();
      e.stopPropagation();
      var handle = heart.getAttribute('data-handle');
      if (!handle) return;
      if (heart.getAttribute('data-sized') === 'true') { sizedHeartClick(heart, handle); return; }
      if (has(handle)) {
        remove(handle);
        setStatus('');
      } else {
        var vid = pdpVariantId(heart);
        add(handle, vid);
        publishAdd(heart, handle, vid);
      }
      pop(heart);
    });
    // The product page reports size changes: the button names the chosen size (#305), so repaint it.
    document.addEventListener('ntf:variant:change', function () {
      $$('[data-wishlist-heart-pdp]').forEach(paintHeart);
    });
    document.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('form[data-gift-voucher-form] input[name="id"]')) $$('[data-wishlist-heart-pdp]').forEach(paintHeart);
    });
    // Cards rendered later (search, wishlist page) get their state too.
    var main = $('#main') || document.body;
    if (window.MutationObserver) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; renderHearts(); });
      }).observe(main, { childList: true, subtree: true });
    }
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) { mem = null; renderHearts(); renderCount(); }
    });
  }

  /* ------- Sizes (#305): one entry per size, the card size picker, the second-size prompt -------
     A piece with more than one variant carries data-sized, data-option-name and data-variants
     ([id, title, available] each) on its heart. Words come from the option: "ring size J",
     "Large bangle", "£20 voucher". Saving a first size, or tapping a saved size, never prompts;
     saving another size of a piece already saved asks Keep / Change to / Save both. */
  function sizeKindOf(optionName, list) {
    var n = String(optionName || '').toLowerCase();
    if (/bangle/.test(n)) return 'bangle';
    if (/denomination|amount/.test(n)) return 'voucher';
    if (n === 'ring size' || (n === 'size' && list.length && list.every(function (v) { return /^[A-Z]{1,2}(\s?(½|1\/2))?\+?$/.test(v.title); }))) return 'ring';
    return 'other';
  }
  function wordsFor(kind, title) {
    if (kind === 'ring') return { short: title, phrase: 'ring size ' + title };
    if (kind === 'bangle') { var s = title.replace(/\s*bangle\s*$/i, '') || title; return { short: s, phrase: s + ' bangle' }; }
    if (kind === 'voucher') return { short: title, phrase: title + ' voucher' };
    return { short: title, phrase: title };
  }
  function capitalise(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function listWords(arr) { return arr.length < 2 ? arr.join('') : arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1]; }
  function numberWord(n) { return ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'][n] || String(n); }
  function chooseWords(kind) { return kind === 'voucher' ? 'an amount' : 'a size'; }

  // Parsed once per element (re-parsed if the attribute changes); [{id, title, available}].
  function variantsOf(el) {
    var raw = el.getAttribute('data-variants') || '[]';
    if (el._ntfRaw !== raw) {
      var list = [];
      try { list = JSON.parse(raw).map(function (v) { return { id: String(v[0]), title: String(v[1]), available: !!v[2] }; }); } catch (e) { list = []; }
      el._ntfRaw = raw; el._ntfVariants = list; el._ntfKind = sizeKindOf(el.getAttribute('data-option-name'), list);
    }
    return el._ntfVariants;
  }
  function heartVariant(el, id) { if (!id) return null; var l = variantsOf(el); for (var i = 0; i < l.length; i++) if (l[i].id === String(id)) return l[i]; return null; }
  function sizeKind(el) { variantsOf(el); return el._ntfKind; }
  function sizeWords(el, title) { return wordsFor(sizeKind(el), title); }

  function alreadyText(kind, shorts) {
    var l = listWords(shorts);
    if (kind === 'ring') return 'You’ve already saved ' + (shorts.length > 1 ? 'sizes ' : 'size ') + l + ' of this ring.';
    if (kind === 'bangle') return 'You’ve already saved this bangle in ' + l + '.';
    if (kind === 'voucher') return 'You’ve already saved ' + (shorts.length > 1 ? l + ' vouchers.' : 'a ' + l + ' voucher.');
    return 'You’ve already saved this piece in ' + l + '.';
  }

  // Every saved size of the piece becomes toVid (one entry, earliest addedAt): "Change to K".
  function changeSize(handle, toVid) {
    var state = read(), keep = null;
    state.items = state.items.filter(function (it) {
      if (it.handle !== handle || !it.variantId) return true;
      if (!keep) { keep = it; return true; }
      if (it.addedAt < keep.addedAt) keep.addedAt = it.addedAt;
      return false;
    });
    if (!keep) return;
    keep.variantId = String(toVid);
    state.items = cap(dedupe(state.items));
    write(state);
    afterChange();
  }

  // One entry changes size in place (an old no-size entry given a size); merges if that size is already saved.
  // Returns 'merged' when the entry folded into an existing one.
  function setSize(handle, fromVid, toVid) {
    var state = read(), from = find(handle, fromVid), to = find(handle, toVid);
    if (!from || from === to) return '';
    if (to) {
      if (from.addedAt < to.addedAt) to.addedAt = from.addedAt;
      state.items = state.items.filter(function (it) { return it !== from; });
    } else {
      from.variantId = String(toVid);
    }
    write(state);
    afterChange();
    return to ? 'merged' : 'changed';
  }

  var liveEl = null;
  function announce(text) {
    if (statusEl) { setStatus(text); return; }
    if (!liveEl) {
      liveEl = document.createElement('div');
      liveEl.className = 'visually-hidden';
      liveEl.setAttribute('role', 'status');
      liveEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveEl);
    }
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = text; }, 50); // a fresh write, so the same words are read twice if repeated
  }

  // Saves one size, asking first when another size of the piece is already saved. ask(heart, others, size, resolve)
  // shows the prompt and calls resolve('keep' | 'change' | 'both'); done(choice) runs after the list changes.
  function saveSize(heart, handle, size, ask, done) {
    var phrase = sizeWords(heart, size.title).phrase;
    var title = heart.getAttribute('data-title') || 'this piece';
    var order = variantsOf(heart).map(function (v) { return v.id; });
    var others = sizesOf(handle).filter(function (it) { return it.variantId !== size.id; })
      .sort(function (a, b) { return order.indexOf(a.variantId) - order.indexOf(b.variantId); }); // J and K, not K and J
    if (!others.length) {
      // An entry saved before sizes counted takes the size in place; otherwise a new entry.
      if (has(handle, null)) setSize(handle, null, size.id); else add(handle, size.id);
      publishAdd(heart, handle, size.id);
      pop(heart);
      announce(capitalise(phrase) + ' of ' + title + ' saved to your wishlist.');
      if (done) done('saved');
      return;
    }
    ask(heart, others, size, function (choice) {
      if (choice === 'change') {
        changeSize(handle, size.id);
        publishAdd(heart, handle, size.id);
        pop(heart);
        announce('Changed to ' + phrase + '.');
      } else if (choice === 'both') {
        add(handle, size.id);
        publishAdd(heart, handle, size.id);
        pop(heart);
        announce(capitalise(phrase) + ' saved too.');
      }
      if (done) done(choice);
    });
  }

  // The prompt's words and buttons, shared by the card picker and the product page dialog.
  function fillPrompt(box, heart, others, size, resolve) {
    var kind = sizeKind(heart);
    var shorts = others.map(function (it) { var v = heartVariant(heart, it.variantId); return v ? sizeWords(heart, v.title).short : '?'; });
    var newShort = sizeWords(heart, size.title).short;
    var id = 'wishlist-prompt-' + Math.random().toString(36).slice(2, 8);
    box.innerHTML = '';
    box.setAttribute('role', 'group');
    box.setAttribute('aria-labelledby', id);
    var p = document.createElement('p');
    p.className = 'wishlist-prompt__text';
    p.id = id;
    p.textContent = alreadyText(kind, shorts);
    var row = document.createElement('div');
    row.className = 'wishlist-prompt__buttons';
    var primary = null;
    [['keep', 'Keep ' + listWords(shorts), 'btn btn--ghost btn--sm'],
     ['change', 'Change to ' + newShort, 'btn btn--primary btn--sm'],
     ['both', others.length === 1 ? 'Save both' : 'Save ' + newShort + ' too', 'btn btn--ghost btn--sm']].forEach(function (b) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = b[2];
      btn.textContent = b[1];
      btn.addEventListener('click', function () { resolve(b[0]); });
      if (b[0] === 'change') primary = btn;
      row.appendChild(btn);
    });
    box.appendChild(p);
    box.appendChild(row);
    return primary;
  }

  function closeIcon() { return '<svg class="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>'; }

  // Product page and shared-list hearts: the prompt is a small dialog like the share one.
  var promptDialog = null;
  function askInDialog(heart, others, size, resolve) {
    if (!promptDialog) {
      promptDialog = document.createElement('dialog');
      promptDialog.className = 'wishlist-share wishlist-prompt';
      promptDialog.setAttribute('aria-labelledby', 'wishlist-prompt-title');
      promptDialog.innerHTML = '<div class="wishlist-share__head"><h2 class="wishlist-share__title" id="wishlist-prompt-title"></h2>' +
        '<button type="button" class="wishlist-share__close" aria-label="Close" data-wishlist-prompt-close>' + closeIcon() + '</button></div>' +
        '<div class="wishlist-share__body" data-wishlist-prompt-body></div>';
      document.body.appendChild(promptDialog);
      $('[data-wishlist-prompt-close]', promptDialog).addEventListener('click', function () { promptDialog.close(); });
      promptDialog.addEventListener('click', function (e) { if (e.target === promptDialog) promptDialog.close(); });
    }
    var answered = false;
    var finish = function (choice) {
      if (answered) return;
      answered = true;
      promptDialog.removeEventListener('close', onClose);
      if (promptDialog.open) promptDialog.close();
      resolve(choice);
      heart.focus();
    };
    var onClose = function () { finish('keep'); }; // Escape, the close button or the backdrop: nothing changes
    $('#wishlist-prompt-title', promptDialog).textContent = sizeKind(heart) === 'voucher' ? 'Save another amount?' : 'Save another size?';
    var primary = fillPrompt($('[data-wishlist-prompt-body]', promptDialog), heart, others, size, finish);
    promptDialog.addEventListener('close', onClose);
    if (promptDialog.showModal) promptDialog.showModal(); else promptDialog.setAttribute('open', '');
    if (primary) primary.focus();
  }

  // Card hearts: a picker anchored to the heart (a bottom sheet on phones). Lives on <body>, outside #main,
  // so filling it never wakes the heart MutationObserver.
  var picker = null, pickerHeart = null;
  var PHONE = '(max-width: 600px)';
  function buildPicker() {
    picker = document.createElement('dialog');
    picker.className = 'wishlist-picker';
    picker.setAttribute('aria-labelledby', 'wishlist-picker-title');
    picker.innerHTML = '<div class="wishlist-picker__head"><div><h2 class="wishlist-picker__title" id="wishlist-picker-title"></h2>' +
      '<p class="wishlist-picker__for" data-picker-for></p></div>' +
      '<button type="button" class="wishlist-share__close" aria-label="Close" data-picker-close>' + closeIcon() + '</button></div>' +
      '<div data-picker-list><div class="wishlist-picker__sizes" role="group" data-picker-sizes></div>' +
      '<a class="wishlist-picker__guide" data-picker-guide hidden>Not sure of your size?</a></div>' +
      '<div class="wishlist-prompt" data-picker-prompt hidden></div>';
    document.body.appendChild(picker);
    $('[data-picker-close]', picker).addEventListener('click', function () { closePicker(true); });
    picker.addEventListener('cancel', function (e) { e.preventDefault(); closePicker(true); }); // Escape on the sheet
    picker.addEventListener('click', function (e) {
      if (e.target === picker) { closePicker(true); return; } // the sheet's backdrop
      var chip = e.target.closest('[data-picker-size]');
      if (chip && !chip.disabled) pickSize(chip.getAttribute('data-picker-size'));
    });
    picker.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); closePicker(true); }
    });
    // The anchored picker is not modal: a tap or focus outside closes it.
    document.addEventListener('pointerdown', function (e) {
      if (picker.open && !picker.classList.contains('wishlist-picker--sheet') && !picker.contains(e.target) && !(pickerHeart && pickerHeart.contains(e.target))) closePicker(false);
    });
    picker.addEventListener('focusout', function (e) {
      if (e.relatedTarget && !picker.contains(e.relatedTarget) && !(pickerHeart && pickerHeart.contains(e.relatedTarget))) closePicker(false);
    });
    window.addEventListener('resize', function () { if (picker.open && !picker.classList.contains('wishlist-picker--sheet')) closePicker(false); });
  }

  function closePicker(returnFocus) {
    if (!picker || !picker.open) return;
    var heart = pickerHeart;
    pickerHeart = null; // cleared here, not on the close event, which arrives after a reopen on another heart
    if (heart) heart.setAttribute('aria-expanded', 'false');
    if (picker.close) picker.close(); else picker.removeAttribute('open');
    if (returnFocus && heart) heart.focus();
  }

  function fillSizes(heart) {
    var handle = heart.getAttribute('data-handle');
    var box = $('[data-picker-sizes]', picker);
    var kind = sizeKind(heart);
    box.innerHTML = '';
    box.setAttribute('aria-label', kind === 'voucher' ? 'Amounts' : 'Sizes');
    variantsOf(heart).forEach(function (v) {
      var on = has(handle, v.id);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wishlist-picker__size' + (kind === 'ring' ? ' wishlist-picker__size--ring' : '');
      btn.setAttribute('data-picker-size', v.id);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', capitalise(sizeWords(heart, v.title).phrase) + (on ? ', saved' : '') + (v.available ? '' : ', sold out'));
      btn.textContent = sizeWords(heart, v.title).short;
      if (!v.available && !on) btn.disabled = true; // a saved size that sold out can still be taken off
      box.appendChild(btn);
    });
  }

  function openPicker(heart) {
    if (!picker) buildPicker();
    if (picker.open) { var was = pickerHeart; closePicker(false); if (was === heart) return; }
    pickerHeart = heart;
    var kind = sizeKind(heart);
    $('#wishlist-picker-title', picker).textContent = 'Choose ' + chooseWords(kind);
    $('[data-picker-for]', picker).textContent = heart.getAttribute('data-title') || '';
    var guide = $('[data-picker-guide]', picker);
    guide.hidden = kind !== 'ring';
    guide.href = body.getAttribute('data-ring-guide-url') || '/pages/ring-size-guide';
    $('[data-picker-list]', picker).hidden = false;
    $('[data-picker-prompt]', picker).hidden = true;
    fillSizes(heart);
    var sheet = window.matchMedia && window.matchMedia(PHONE).matches;
    picker.classList.toggle('wishlist-picker--sheet', !!sheet);
    picker.style.left = ''; picker.style.top = '';
    heart.setAttribute('aria-expanded', 'true');
    if (sheet && picker.showModal) picker.showModal();
    else { if (picker.show) picker.show(); else picker.setAttribute('open', ''); placePicker(heart); }
    var first = $('[data-picker-size][aria-pressed="true"]', picker) || $('[data-picker-size]:not([disabled])', picker);
    if (first) first.focus();
  }

  // Below the heart, right edges aligned, kept on screen; above it when there is no room below.
  function placePicker(heart) {
    var r = heart.getBoundingClientRect(), w = picker.offsetWidth, h = picker.offsetHeight;
    var vw = document.documentElement.clientWidth;
    var left = Math.max(8, Math.min(r.right - w, vw - w - 8));
    var top = r.bottom + 8;
    if (top + h > window.innerHeight - 8 && r.top - h - 8 > 8) top = r.top - h - 8;
    picker.style.left = (left + window.pageXOffset) + 'px';
    picker.style.top = (top + window.pageYOffset) + 'px';
  }

  function pickSize(vid) {
    var heart = pickerHeart;
    if (!heart) return;
    var handle = heart.getAttribute('data-handle');
    var size = heartVariant(heart, vid);
    if (!size) return;
    if (has(handle, vid)) {
      remove(handle, vid);
      pop(heart);
      announce(capitalise(sizeWords(heart, size.title).phrase) + ' removed from your wishlist.');
      closePicker(true);
      return;
    }
    saveSize(heart, handle, size, askInPicker, function () { closePicker(true); });
  }

  // The second-size prompt replaces the size list inside the picker.
  function askInPicker(heart, others, size, resolve) {
    var box = $('[data-picker-prompt]', picker);
    var primary = fillPrompt(box, heart, others, size, resolve);
    box.hidden = false;
    $('[data-picker-list]', picker).hidden = true;
    if (!picker.classList.contains('wishlist-picker--sheet')) placePicker(heart);
    if (primary) primary.focus();
  }

  // Heart taps on pieces with sizes: the card heart opens the picker; the product page button and a
  // one-size heart (shared lists) save or remove that size.
  function sizedHeartClick(heart, handle) {
    var vid = heart.hasAttribute('data-wishlist-heart-pdp') ? pdpVariantId(heart) : heart.getAttribute('data-variant-id');
    if (!heart.hasAttribute('data-wishlist-heart-pdp') && !vid) { openPicker(heart); return; }
    var size = heartVariant(heart, vid);
    if (!size) return;
    if (has(handle, vid)) {
      remove(handle, vid);
      pop(heart);
      announce(capitalise(sizeWords(heart, size.title).phrase) + ' removed from your wishlist.');
      return;
    }
    saveSize(heart, handle, size, askInDialog);
  }

  /* ------- Proxy client (signed-in customers) ------- */
  function request(method, path, payload, timeoutMs, keepalive) {
    var url = path + (path.indexOf('?') > -1 ? '&' : '?') + '_t=' + Date.now();
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, timeoutMs || 5000) : null;
    var init = { method: method, headers: { accept: 'application/json' }, credentials: 'same-origin' };
    if (keepalive) init.keepalive = true;
    if (controller) init.signal = controller.signal;
    if (payload !== undefined) { init.headers['content-type'] = 'application/json'; init.body = JSON.stringify(payload); }
    return fetch(url, init).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
        return { ok: res.ok, status: res.status, body: data };
      });
    }).catch(function () {
      return { ok: false, status: 0, body: null };
    }).then(function (r) { if (timer) clearTimeout(timer); return r; });
  }

  var syncTimer = null;
  function scheduleSync(retry) {
    if (!CUSTOMER_ID) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { doSync(retry); }, 400);
  }

  function doSync(isRetry) {
    var state = read();
    request('PUT', PROXY, { items: state.items }).then(function (res) {
      if (res.ok && res.body) {
        replaceAll(Array.isArray(res.body.items) ? res.body.items : state.items, res.body.savedAt || nowIso());
        renderHearts(); renderCount();
        clearQuietStatus();
      } else if (!isRetry && res.status !== 400) {
        setTimeout(function () { doSync(true); }, 3000);
      } else {
        quietStatus('Saved on this device; will sync when you’re back online.');
      }
    });
  }

  // Union by handle + size (#305); earliest addedAt on exact duplicates; newest 100.
  function merge(local, server) {
    return cap(dedupe(server.concat(local).map(cleanItem).filter(Boolean)));
  }

  function sameList(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i].handle !== b[i].handle || (a[i].variantId || null) !== (b[i].variantId || null) || a[i].addedAt !== b[i].addedAt) return false;
    }
    return true;
  }

  function mergeWithServer(serverItems) {
    var server = serverItems.map(cleanItem).filter(Boolean);
    var merged = merge(read().items, server);
    var differs = !sameList(merged, cap(dedupe(server.slice())));
    replaceAll(merged, differs ? read().syncedAt : nowIso());
    renderHearts(); renderCount();
    if (differs) scheduleSync(false);
  }

  function initAccountSync() {
    if (!CUSTOMER_ID) return;
    var serverEl = $('[data-wishlist-server]');
    if (serverEl) {
      var server = [];
      try { server = JSON.parse(serverEl.textContent || '[]'); } catch (e) { server = []; }
      mergeWithServer(Array.isArray(server) ? server : []);
      return;
    }
    if (!read().syncedAt) {
      request('GET', PROXY).then(function (res) {
        if (res.ok && res.body && Array.isArray(res.body.items)) mergeWithServer(res.body.items);
      });
    }
  }

  /* ------- Sharing ------- */
  function readShareRecord() {
    try { var raw = localStorage.getItem(SHARE_KEY); var r = raw ? JSON.parse(raw) : null; return r && r.id && r.token ? r : null; } catch (e) { return null; }
  }
  function writeShareRecord(rec) { try { if (rec) localStorage.setItem(SHARE_KEY, JSON.stringify(rec)); else localStorage.removeItem(SHARE_KEY); } catch (e) { /* ignore */ } }

  function b64urlEncode(str) {
    var bytes = new TextEncoder().encode(str), bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64urlDecode(str) {
    var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    var bin = atob(b64), bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  }

  // Always on the primary domain; on a preview theme the link carries preview_theme_id, so whoever opens it
  // sees the same theme.
  function absolute(path) {
    var theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.role && theme.role !== 'main' && theme.id) path += (path.indexOf('?') === -1 ? '?' : '&') + 'preview_theme_id=' + theme.id;
    return (/^https?:\/\/[^/]+$/.test(SHOP_URL) ? SHOP_URL : location.origin) + path;
  }

  function fallbackLink(name, items) {
    var slice = items.slice(-URL_LINK_MAX);
    var payload = { n: name || null, i: slice.map(function (it) { return [it.handle, it.variantId || null]; }) };
    var ref = b64urlEncode(JSON.stringify(payload));
    rememberOwnLink('snapshot', ref); // #309
    return { url: absolute(PAGE_URL + '?w=' + ref), live: false, truncated: items.length > URL_LINK_MAX };
  }

  function shareItems() { return getItems().map(function (it) { return { handle: it.handle, variantId: it.variantId }; }); }
  function shareSig(items) { return items.map(function (it) { return it.handle + ':' + (it.variantId || ''); }).join(','); }

  function createShareLink(name) {
    var items = getItems();
    return request('GET', PROXY + '/health', undefined, 2000).then(function (health) {
      if (!health.ok) return fallbackLink(name, items);
      var rec = CUSTOMER_ID ? null : readShareRecord();
      var p = rec
        ? request('PUT', PROXY + '/share/' + rec.id, { token: rec.token, name: name || null, items: shareItems() })
        : request('POST', PROXY + '/share', { name: name || null, items: shareItems() });
      return p.then(function (res) {
        if (rec && res.status === 404) { writeShareRecord(null); return createShareLink(name); }
        if (!res.ok || !res.body) return fallbackLink(name, items);
        var id = res.body.id || (rec && rec.id);
        rememberOwnLink('live', id); // #309
        var sig = shareSig(shareItems());
        if (!CUSTOMER_ID && !rec) writeShareRecord({ id: id, token: res.body.token, name: name || null, sig: sig, updatedAt: nowIso() });
        else if (!CUSTOMER_ID && rec) writeShareRecord({ id: rec.id, token: rec.token, name: name || null, sig: sig, updatedAt: nowIso() });
        return { url: absolute(res.body.url || (PAGE_URL + '?l=' + id)), live: true, truncated: !!res.body.truncated };
      });
    });
  }

  var republishTimer = null;
  // Guests' share links are a copy kept by the worker; signed-in owners' links read the metafield live.
  function republishNow() {
    var rec = readShareRecord();
    if (!rec || CUSTOMER_ID) return;
    var items = shareItems(), sig = shareSig(items);
    if (rec.sig === sig) return;
    request('PUT', PROXY + '/share/' + rec.id, { token: rec.token, name: rec.name || null, items: items }, 5000, true).then(function (res) {
      if (res.status === 404 || res.status === 403) { writeShareRecord(null); return; }
      if (res.ok) { var cur = readShareRecord(); if (cur && cur.id === rec.id) { cur.sig = sig; cur.updatedAt = nowIso(); writeShareRecord(cur); } }
    });
  }
  function scheduleRepublish() {
    if (CUSTOMER_ID || !readShareRecord()) return;
    clearTimeout(republishTimer);
    republishTimer = setTimeout(republishNow, 800);
  }

  function shareText(url) { return 'Here’s my wishlist from Nine Two Five Silver: ' + url; }

  // The last name used to share on this device, offered next time (guests have no first name from Liquid).
  var SHARE_NAME_KEY = 'ntf:wishlist:share-name:v1';
  function cleanName(v) { return String(v || '').replace(/[\u0000-\u001f\u007f-\u009f]/g, '').trim().slice(0, 40); }

  function bindShare(page) {
    var panel = $('[data-wishlist-share]', page);
    if (!panel) return;
    var nameInput = $('[data-wishlist-share-name]', panel);
    var hint = $('[data-wishlist-share-hint]', panel);
    var note = $('[data-wishlist-share-note]', panel);
    var linkBox = $('[data-wishlist-share-link]', panel);
    var methods = $$('[data-wishlist-share-method]', panel);
    var opener = $('[data-wishlist-share-open]', page);
    var current = null, building = false, rebuild = false, nameTimer = null;

    // The name is required (#309): no link is made, and the buttons stay disabled, until there is one.
    function currentName() { return nameInput ? cleanName(nameInput.value) : ''; }
    if (nameInput && !nameInput.value) { try { nameInput.value = cleanName(localStorage.getItem(SHARE_NAME_KEY)); } catch (e) { /* ignore */ } }

    function paint() {
      var named = !!currentName();
      methods.forEach(function (b) { b.disabled = !named || !current; });
      if (hint) hint.hidden = named;
      if (nameInput) nameInput.setAttribute('aria-invalid', named ? 'false' : 'true');
      if (!current && linkBox) linkBox.hidden = true;
      if (!named && note) note.textContent = '';
    }

    function open() {
      if (panel.showModal) panel.showModal(); else panel.setAttribute('open', '');
      if (!currentName()) { paint(); if (nameInput) nameInput.focus(); }
      else if (!current) build();
    }
    function close() { if (panel.close) panel.close(); else panel.removeAttribute('open'); if (opener) opener.focus(); }
    if (opener) opener.addEventListener('click', open);
    $$('[data-wishlist-share-close]', panel).forEach(function (b) { b.addEventListener('click', close); });
    // A click on the backdrop (outside the box) closes it.
    panel.addEventListener('click', function (e) { if (e.target === panel) close(); });

    function build() {
      var name = currentName();
      if (!name) { current = null; paint(); return; }
      if (building) { rebuild = true; return; }
      building = true;
      current = null;
      paint();
      if (note) note.textContent = 'Making your link…';
      createShareLink(name).then(function (link) {
        building = false;
        if (rebuild) { rebuild = false; build(); return; }
        if (currentName() !== name) { build(); return; } // the name changed while the link was being made
        current = link;
        try { localStorage.setItem(SHARE_NAME_KEY, name); } catch (e) { /* ignore */ }
        paint();
        if (linkBox) linkBox.value = link.url;
        var shareBtn = $('[data-wishlist-share-native]', panel);
        if (shareBtn) shareBtn.hidden = !(navigator.share && (!navigator.canShare || navigator.canShare({ url: link.url })));
        if (note) {
          note.textContent = link.live
            ? 'Your link stays up to date when you change your list.'
            : 'Link may be long: sharing works without an account either way.' + (link.truncated ? ' Link carries the first ' + URL_LINK_MAX + ' pieces.' : '');
        }
      });
    }

    if (nameInput) nameInput.addEventListener('input', function () {
      clearTimeout(nameTimer);
      current = null;
      paint();
      if (currentName()) nameTimer = setTimeout(build, 600);
    });
    paint();

    panel.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-share-method]');
      if (!btn || !current) return;
      var method = btn.getAttribute('data-wishlist-share-method');
      var url = current.url, text = shareText(url), title = currentName() + '’s wishlist from Nine Two Five Silver';
      if (method === 'native' && navigator.share) {
        navigator.share({ title: title, text: text, url: url }).then(function () { publish('ntf:share', { method: 'native', content_type: 'wishlist', item_count: getItems().length }); }).catch(function () { /* cancelled */ });
        return;
      }
      if (method === 'copy') {
        var done = function () { if (note) note.textContent = 'Link copied.'; publish('ntf:share', { method: 'copy', content_type: 'wishlist', item_count: getItems().length }); };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { if (linkBox) { linkBox.hidden = false; linkBox.select(); } });
        else if (linkBox) { linkBox.hidden = false; linkBox.select(); try { document.execCommand('copy'); done(); } catch (err) { /* user copies by hand */ } }
        return;
      }
      if (method === 'whatsapp') {
        window.open('https://wa.me/?text=' + encodeURIComponent(text), '_blank', 'noopener');
        publish('ntf:share', { method: 'whatsapp', content_type: 'wishlist', item_count: getItems().length });
        return;
      }
      if (method === 'email') {
        location.href = 'mailto:?subject=' + encodeURIComponent(title) + '&body=' + encodeURIComponent(text);
        publish('ntf:share', { method: 'email', content_type: 'wishlist', item_count: getItems().length });
      }
    });

    // A change to the list while the panel is open rebuilds the fallback link (short links update themselves).
    document.addEventListener('ntf:wishlist:change', function () { if (panel.open && current && !current.live) build(); });
  }

  /* ------- Shared with me (#309) -------
     Every list someone shares that this visitor opens is remembered, so it can be found again from the
     "Shared with me" view without the original link. Guests keep them in localStorage on this device; signed-in
     customers also in the customer metafield wishlist.received, written with PUT /apps/wishlist/received and
     rendered into the page by Liquid ([data-wishlist-received-server]).
     Entry: { kind: 'live' (?l=) | 'snapshot' (?w=), ref: share id or ?w= payload, name, count,
              firstOpenedAt, lastOpenedAt, unavailable? }. At most 20, the least recently opened dropped. */
  var RECEIVED_KEY = 'ntf:wishlist:received:v1';
  var OWN_LINKS_KEY = 'ntf:wishlist:own-links:v1';
  var RECEIVED_MAX = 20;
  var SNAPSHOT_REF_MAX = 3000;
  var receivedMem = null;

  function cleanReceived(raw) {
    if (!raw || (raw.kind !== 'live' && raw.kind !== 'snapshot') || typeof raw.ref !== 'string') return null;
    if (raw.kind === 'live' ? !/^[a-z0-9]{8}$/.test(raw.ref) : !/^[A-Za-z0-9_-]+$/.test(raw.ref) || raw.ref.length > SNAPSHOT_REF_MAX) return null;
    var time = function (t) { return typeof t === 'string' && !isNaN(Date.parse(t)) ? new Date(t).toISOString() : nowIso(); };
    var last = time(raw.lastOpenedAt), first = time(raw.firstOpenedAt || raw.lastOpenedAt);
    var entry = {
      kind: raw.kind, ref: raw.ref,
      name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim().slice(0, 40) : null,
      count: typeof raw.count === 'number' && raw.count >= 0 ? Math.floor(raw.count) : null,
      firstOpenedAt: first < last ? first : last, lastOpenedAt: last
    };
    if (raw.unavailable === true) entry.unavailable = true;
    return entry;
  }
  function receivedId(e) { return e.kind + ':' + e.ref; }

  // Union by kind+ref; earliest first opened; name, count and availability from whichever was opened last
  // (the first list wins a tie, so pass the account's copy first); the newest 20, oldest-opened first.
  function mergeReceived(first, second) {
    var byId = {}, out = [];
    first.concat(second).forEach(function (raw) {
      var e = cleanReceived(raw);
      if (!e) return;
      var ex = byId[receivedId(e)];
      if (!ex) { byId[receivedId(e)] = e; out.push(e); return; }
      if (e.firstOpenedAt < ex.firstOpenedAt) ex.firstOpenedAt = e.firstOpenedAt;
      if (e.lastOpenedAt > ex.lastOpenedAt) {
        ex.lastOpenedAt = e.lastOpenedAt; ex.name = e.name; ex.count = e.count;
        if (e.unavailable) ex.unavailable = true; else delete ex.unavailable;
      }
    });
    out.sort(function (a, b) { return a.lastOpenedAt < b.lastOpenedAt ? -1 : a.lastOpenedAt > b.lastOpenedAt ? 1 : 0; });
    return out.length > RECEIVED_MAX ? out.slice(out.length - RECEIVED_MAX) : out;
  }

  // customer: the account this device last synced with (null for a guest's lists); syncedAt: when.
  function readReceived() {
    if (receivedMem) return receivedMem;
    var data = null;
    try { var raw = localStorage.getItem(RECEIVED_KEY); data = raw ? JSON.parse(raw) : null; } catch (e) { data = null; }
    receivedMem = {
      v: 1,
      lists: mergeReceived(data && Array.isArray(data.lists) ? data.lists : [], []),
      syncedAt: data && data.syncedAt ? data.syncedAt : null,
      customer: data && data.customer ? String(data.customer) : null
    };
    return receivedMem;
  }
  function writeReceived(state) {
    receivedMem = state;
    try { localStorage.setItem(RECEIVED_KEY, JSON.stringify(state)); } catch (e) { /* private mode or blocked: memory only */ }
  }
  function findReceived(kind, ref) {
    var lists = readReceived().lists;
    for (var i = 0; i < lists.length; i++) if (lists[i].kind === kind && lists[i].ref === ref) return lists[i];
    return null;
  }

  // Links this device made with Share, so opening your own link does not file it under "Shared with me".
  // A guest's live link is also in the share record; a signed-in owner's is recognised by the worker (mine: true).
  function readOwnLinks() {
    try { var raw = localStorage.getItem(OWN_LINKS_KEY); var r = raw ? JSON.parse(raw) : []; return Array.isArray(r) ? r : []; } catch (e) { return []; }
  }
  function rememberOwnLink(kind, ref) {
    var own = readOwnLinks().filter(function (x) { return x !== kind + ':' + ref; });
    own.push(kind + ':' + ref);
    try { localStorage.setItem(OWN_LINKS_KEY, JSON.stringify(own.slice(-10))); } catch (e) { /* ignore */ }
  }
  function isOwnLink(kind, ref) {
    var rec = readShareRecord();
    if (kind === 'live' && rec && rec.id === ref) return true;
    return readOwnLinks().indexOf(kind + ':' + ref) > -1;
  }

  function rememberShared(kind, ref, name, count, mine) {
    if (mine || isOwnLink(kind, ref)) return;
    if (kind === 'snapshot' && ref.length > SNAPSHOT_REF_MAX) return;
    var state = readReceived(), now = nowIso();
    var ex = findReceived(kind, ref);
    if (ex) {
      ex.name = name || null; ex.count = count; ex.lastOpenedAt = now;
      delete ex.unavailable;
      state.lists = mergeReceived(state.lists, []);
    } else {
      state.lists = mergeReceived(state.lists, [{ kind: kind, ref: ref, name: name || null, count: count, firstOpenedAt: now, lastOpenedAt: now }]);
    }
    writeReceived(state);
    scheduleReceivedSync();
  }

  function markReceivedUnavailable(kind, ref) {
    var ex = findReceived(kind, ref);
    if (!ex || ex.unavailable) return;
    ex.unavailable = true;
    writeReceived(readReceived());
    scheduleReceivedSync();
  }

  function removeReceived(kind, ref) {
    var state = readReceived();
    state.lists = state.lists.filter(function (e) { return !(e.kind === kind && e.ref === ref); });
    writeReceived(state);
    scheduleReceivedSync();
  }

  var receivedSyncTimer = null;
  function scheduleReceivedSync() {
    if (!CUSTOMER_ID) return;
    clearTimeout(receivedSyncTimer);
    receivedSyncTimer = setTimeout(function () { doReceivedSync(false); }, 400);
  }
  function doReceivedSync(isRetry) {
    var state = readReceived();
    var payload = { lists: state.lists };
    // keepalive lets the write finish if the page is left straight away, but browsers cap such bodies at 64 KB.
    var small = JSON.stringify(payload).length < 60000;
    request('PUT', PROXY + '/received', payload, 5000, small).then(function (res) {
      if (res.ok) {
        var cur = readReceived();
        cur.syncedAt = nowIso(); cur.customer = CUSTOMER_ID;
        writeReceived(cur);
      } else if (!isRetry && res.status !== 400 && res.status !== 401 && res.status !== 413) {
        setTimeout(function () { doReceivedSync(true); }, 3000);
      }
    });
  }

  // Signing in merges this device's lists into the account's. Once a device has synced with this account, the
  // account's copy wins (so a list removed on another device stays removed) apart from lists opened here since.
  // Liquid renders null when the metafield is missing or unreadable; that is treated as "unknown", never as empty.
  function initReceivedSync() {
    if (!CUSTOMER_ID) return;
    var el = $('[data-wishlist-received-server]');
    if (!el) return;
    var server = null;
    try { server = JSON.parse(el.textContent || 'null'); } catch (e) { server = null; }
    var known = Array.isArray(server);
    var state = readReceived(), local = state.lists;
    if (state.customer && state.customer !== CUSTOMER_ID) local = []; // another account's lists stay out of this one
    else if (known && state.customer === CUSTOMER_ID && state.syncedAt) local = local.filter(function (e) { return e.lastOpenedAt > state.syncedAt; });
    var base = mergeReceived(known ? server : [], []);
    var merged = mergeReceived(known ? server : [], local);
    var differs = JSON.stringify(merged) !== JSON.stringify(base);
    state.lists = merged;
    state.customer = CUSTOMER_ID;
    if (known && !differs) state.syncedAt = nowIso();
    writeReceived(state);
    if (differs) scheduleReceivedSync();
  }

  // A list with no name (an old link, or a long link made before names were required) is told apart by the
  // day it was first opened here.
  function receivedTitle(e) {
    if (e.name) return e.name + '’s wishlist';
    var d = openedDate(e.firstOpenedAt || nowIso());
    return d === 'today' ? 'Wishlist shared today' : 'Wishlist shared on ' + d;
  }
  function receivedUrl(e) { return PAGE_URL + (e.kind === 'live' ? '?l=' : '?w=') + e.ref; }
  function openedDate(iso) {
    var d = new Date(iso), today = new Date();
    if (d.toDateString() === today.toDateString()) return 'today';
    try { return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { return d.toDateString(); }
  }

  /* Views (#309): your list ('own'), Shared with me ('received', #shared-with-me) and a list someone sent
     ('shared', ?l= / ?w=). All three use the one header; every action, including the switch between views,
     is a button in .wishlist__bar. */
  var view = { name: 'own', ownTitle: '', pieces: 0, unavailable: false, refreshed: false };

  function setHeader(eyebrow, title) {
    if (pageEls.eyebrow) pageEls.eyebrow.textContent = eyebrow;
    if (pageEls.title) pageEls.title.textContent = title;
  }

  // Shows the buttons this view needs, with their counts, and the count line under the title.
  function renderBar() {
    var v = view.name, own = read().items.length, received = readReceived().lists.length;
    if (pageEls.addAll) pageEls.addAll.hidden = v === 'received' || !(pageEls.grid && $('[data-buy-id]', pageEls.grid));
    if (pageEls.shareOpen) pageEls.shareOpen.hidden = v !== 'own' || own === 0;
    if (pageEls.ownLink) pageEls.ownLink.hidden = v === 'own';
    if (pageEls.receivedLink) pageEls.receivedLink.hidden = v === 'received' || received === 0;
    if (pageEls.ownCount) pageEls.ownCount.textContent = '(' + own + ')';
    if (pageEls.receivedCount) pageEls.receivedCount.textContent = '(' + received + ')';
    if (pageEls.signin) pageEls.signin.hidden = !!CUSTOMER_ID || v !== 'own';
    if (!pageEls.count) return;
    var text = v === 'received' ? (received === 1 ? '1 list' : received + ' lists') : view.unavailable ? '' : view.pieces === 1 ? '1 piece' : view.pieces + ' pieces';
    if (pageEls.count.textContent !== text) pageEls.count.textContent = text;
  }

  // Your list or Shared with me, in place. focus: after a click, the title takes focus so the new view is read out.
  function showView(name, focus) {
    view.name = name;
    $$('[data-wishlist-panel]').forEach(function (p) { p.hidden = p.getAttribute('data-wishlist-panel') !== name; });
    if (name === 'received') { setHeader('Shared with you', 'Wishlists shared with you'); renderReceived(); refreshReceived(); }
    else setHeader('Your wishlist', view.ownTitle);
    renderBar();
    if (focus && pageEls.title) pageEls.title.focus();
  }

  // The address keeps #shared-with-me, so reload and Back return to the same view.
  function initViews(page) {
    $$('[data-wishlist-view-link]', page).forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // a new tab or window
        e.preventDefault();
        var name = a.getAttribute('data-wishlist-view-link');
        try { history.pushState(history.state, '', location.pathname + location.search + (name === 'received' ? '#shared-with-me' : '')); } catch (err) { /* ignore */ }
        showView(name, true);
      });
    });
    window.addEventListener('popstate', function () { showView(location.hash === '#shared-with-me' ? 'received' : 'own', true); });
    showView(location.hash === '#shared-with-me' ? 'received' : 'own', false);
  }

  function renderReceived() {
    var ul = $('[data-wishlist-received-list]');
    if (!ul) return;
    // Keep keyboard focus on the same button when the rows are rebuilt.
    var active = document.activeElement, keep = active && ul.contains(active) ? { id: active.getAttribute('data-received-id'), action: active.getAttribute('data-received-action') } : null;
    var lists = readReceived().lists.slice().reverse(); // most recently opened first
    ul.innerHTML = '';
    lists.forEach(function (e) {
      var li = document.createElement('li');
      li.className = 'wishlist-received__row' + (e.unavailable ? ' is-unavailable' : '');
      var text = document.createElement('div');
      text.className = 'wishlist-received__text';
      var h = document.createElement('h2');
      h.className = 'wishlist-received__name';
      h.textContent = receivedTitle(e);
      var meta = document.createElement('p');
      meta.className = 'wishlist-received__meta';
      var when = openedDate(e.firstOpenedAt), bits = ['Received ' + when];
      if (e.unavailable) bits.push('No longer available');
      else if (e.count != null) bits.push(e.count === 1 ? '1 piece' : e.count + ' pieces');
      meta.textContent = bits.join(' · ');
      text.appendChild(h); text.appendChild(meta);
      var actions = document.createElement('div');
      actions.className = 'wishlist-received__actions';
      if (!e.unavailable) {
        var open = document.createElement('a');
        open.className = 'btn btn--primary btn--sm';
        open.href = receivedUrl(e);
        open.setAttribute('data-received-id', receivedId(e)); open.setAttribute('data-received-action', 'view');
        open.innerHTML = 'View<span class="visually-hidden"> ' + esc(receivedTitle(e)) + '</span>';
        actions.appendChild(open);
      }
      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'btn btn--ghost btn--sm';
      rm.setAttribute('data-received-id', receivedId(e)); rm.setAttribute('data-received-action', 'remove');
      rm.innerHTML = 'Remove from this list<span class="visually-hidden">: ' + esc(receivedTitle(e)) + '</span>';
      rm.addEventListener('click', function () {
        // Focus moves to the next row (or the previous, or the title) so keyboard users are not dropped.
        var row = rm.closest('li'), target = row.nextElementSibling || row.previousElementSibling;
        removeReceived(e.kind, e.ref);
        renderReceived();
        renderBar();
        setStatus(receivedTitle(e) + ' removed from Shared with me.');
        var focusEl = target && $('[data-received-id="' + target.querySelector('[data-received-id]').getAttribute('data-received-id') + '"]', ul);
        if (focusEl) focusEl.focus(); else if (pageEls.title) pageEls.title.focus();
      });
      actions.appendChild(rm);
      li.appendChild(text); li.appendChild(actions);
      ul.appendChild(li);
    });
    var empty = $('[data-wishlist-received-empty]');
    if (empty) empty.hidden = lists.length > 0;
    ul.hidden = !lists.length;
    if (keep && keep.id) {
      var again = $('[data-received-id="' + keep.id + '"][data-received-action="' + keep.action + '"]', ul) || $('[data-received-id="' + keep.id + '"]', ul);
      if (again) again.focus();
    }
  }

  // Once per page, when Shared with me is first shown: re-read live links for the current count and name, and
  // mark the ones that have expired or been deleted. Three at a time; a network failure changes nothing.
  function refreshReceived() {
    if (view.refreshed) return;
    view.refreshed = true;
    var live = readReceived().lists.filter(function (e) { return e.kind === 'live'; });
    if (!live.length) return;
    var changed = false;
    inBatches(live, 3, function (e) {
      return request('GET', PROXY + '/share/' + e.ref).then(function (res) {
        var cur = findReceived(e.kind, e.ref);
        if (!cur) return;
        if (res.status === 404 && !cur.unavailable) { cur.unavailable = true; changed = true; }
        else if (res.ok && res.body && Array.isArray(res.body.items)) {
          if (res.body.mine) { removeReceived(cur.kind, cur.ref); changed = true; return; }
          var name = cleanName(res.body.name) || null, count = res.body.items.length;
          if (cur.unavailable || cur.name !== name || cur.count !== count) { delete cur.unavailable; cur.name = name; cur.count = count; changed = true; }
        }
      });
    }).then(function () {
      if (!changed) return;
      writeReceived(readReceived());
      scheduleReceivedSync();
      renderBar();
      if (view.name === 'received') renderReceived();
    });
  }

  /* ------- The wishlist page ------- */
  var statusEl = null, quiet = '';
  function setStatus(text) { if (statusEl) statusEl.textContent = text || quiet; }
  function quietStatus(text) { quiet = text; if (statusEl && !statusEl.textContent) statusEl.textContent = text; }
  function clearQuietStatus() { if (statusEl && statusEl.textContent === quiet) statusEl.textContent = ''; quiet = ''; }

  function fetchCard(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=wishlist-card', { headers: { accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (res) {
        if (res.status === 404) return { handle: handle, status: 404 };
        if (!res.ok) return { handle: handle, status: res.status };
        return res.text().then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-wishlist-card]');
          if (!node) return { handle: handle, status: 404 };
          var variants = [];
          try { variants = JSON.parse((doc.querySelector('[data-wishlist-variants]') || {}).textContent || '[]'); } catch (e) { variants = []; }
          return { handle: handle, status: 200, node: node, variants: variants, available: node.getAttribute('data-available') !== 'false', url: node.getAttribute('data-url') || ('/products/' + handle), title: node.getAttribute('data-title') || handle, hasOptions: node.getAttribute('data-has-options') === 'true', optionName: node.getAttribute('data-option-name') || '' };
        });
      })
      .catch(function () { return { handle: handle, status: 0 }; });
  }

  function inBatches(items, size, fn) {
    var results = [];
    function next(i) {
      if (i >= items.length) return Promise.resolve(results);
      var slice = items.slice(i, i + size);
      return Promise.all(slice.map(function (it) { return fn(it).then(function (r) { return r; }, function () { return { handle: it.handle, status: 0 }; }); }))
        .then(function (rs) { results = results.concat(rs); return next(i + size); });
    }
    return next(0);
  }

  function addToBag(variantId, button) {
    if (button) button.disabled = true;
    var done = function () { if (button) button.disabled = false; };
    if (window.NTF && NTF.addToCart) {
      NTF.addToCart(variantId, 1).then(function () { return NTF.refreshCart(); }).then(function () { NTF.openDrawer(); done(); }).catch(function () { done(); location.href = '/cart/add?id=' + variantId + '&quantity=1'; });
    } else {
      location.href = '/cart/add?id=' + variantId + '&quantity=1';
    }
  }

  function addAllToBag(button) {
    var ids = $$('[data-buy-id]', pageEls.grid).map(function (c) { return c.getAttribute('data-buy-id'); });
    var skipped = $$('.wishlist-cell', pageEls.grid).length - ids.length;
    if (!ids.length) return;
    if (!(window.NTF && NTF.addToCart)) { location.href = '/cart/add?id=' + ids[0] + '&quantity=1'; return; }
    button.disabled = true;
    setStatus('Adding to your bag…');
    var added = 0, failed = 0, already = 0;
    fetch('/cart.js', { headers: { accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); }, function () { return { items: [] }; })
      .then(function (cart) {
        var inBag = {};
        (cart.items || []).forEach(function (line) { inBag[String(line.variant_id)] = true; });
        return ids.reduce(function (p, id) {
          return p.then(function () {
            if (inBag[String(id)]) { already++; return; }
            return NTF.addToCart(id, 1).then(function (r) { if (r && r.id && !r.status) added++; else failed++; }, function () { failed++; });
          });
        }, Promise.resolve());
      })
      .then(function () { return NTF.refreshCart(); })
      .catch(function () { /* the status line below still reports what happened */ })
      .then(function () {
        button.disabled = false;
        var parts = [];
        if (added) parts.push(added === 1 ? 'Added 1 piece to your bag.' : 'Added ' + added + ' pieces to your bag.');
        if (already) parts.push(already === 1 ? '1 piece was already in your bag.' : already + ' pieces were already in your bag.');
        if (failed) parts.push(failed === 1 ? '1 piece could not be added.' : failed + ' pieces could not be added.');
        if (skipped) parts.push(skipped === 1 ? '1 piece needs a size choosing or is sold out.' : skipped + ' pieces need a size choosing or are sold out.');
        setStatus(parts.join(' '));
        if (added || already) NTF.openDrawer();
      });
  }

  /* ------- Wishlist page sizes (#305): size words, the size dropdown for no-size entries, the two-sizes note ------- */
  function pageVariants(info) { return (info.variants || []).map(function (v) { return { id: String(v.id), title: String(v.title), available: !!v.available }; }); }
  function sizeLabel(kind, optionName, title) { return kind === 'other' ? (optionName ? optionName + ': ' : '') + title : capitalise(wordsFor(kind, title).phrase); }

  // Re-renders one card's actions after its entry changes size.
  function redecorate(card) {
    var ctx = card._ntf;
    if (!ctx) return;
    var row = $('.wishlist-card__actions', card);
    if (row) row.remove();
    card.removeAttribute('data-buy-id');
    decorateCard(card, ctx.item, ctx.info, ctx.mode, ctx.template);
  }

  function sizeSelect(card, item, info, list, kind) {
    var wrap = document.createElement('label');
    wrap.className = 'wishlist-card__pick';
    var hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'visually-hidden';
    hiddenLabel.textContent = 'Choose ' + chooseWords(kind) + ' for ' + info.title;
    var select = document.createElement('select');
    select.className = 'wishlist-card__select';
    var none = document.createElement('option');
    none.value = '';
    none.textContent = 'Choose ' + chooseWords(kind);
    select.appendChild(none);
    list.forEach(function (v) {
      var o = document.createElement('option');
      o.value = v.id;
      o.textContent = capitalise(wordsFor(kind, v.title).phrase) + (v.available ? '' : ' (sold out)');
      if (!v.available) o.disabled = true;
      select.appendChild(o);
    });
    select.addEventListener('change', function () {
      var vid = select.value;
      if (!vid) return;
      var v = null;
      list.forEach(function (x) { if (x.id === vid) v = x; });
      var phrase = capitalise(wordsFor(kind, v ? v.title : vid).phrase);
      if (card._ntf.mode === 'shared') {
        // Someone else's list: the choice only says which size "Buy this" adds.
        item.variantId = vid;
        redecorate(card);
        setStatus(phrase + ' chosen.');
        return;
      }
      if (setSize(item.handle, null, vid) === 'merged') {
        // That size was already saved: the two entries are one now, so this card goes.
        card.remove();
        updateSummary('own', getItems().length);
        setStatus(info.title + ': ' + phrase + ' was already saved.');
      } else {
        item.variantId = vid;
        redecorate(card);
        setStatus(info.title + ': ' + phrase + ' saved.');
        var focusTo = $('.wishlist-card__actions button, .wishlist-card__actions a', card);
        if (focusTo) focusTo.focus(); // the dropdown has gone; keep the keyboard on this card
      }
      updateSummary('own', getItems().length);
      paintSizeNotes('own');
    });
    wrap.appendChild(hiddenLabel);
    wrap.appendChild(select);
    return wrap;
  }

  // Two or more sizes of one piece: "Saved in two sizes" on your own list; on a shared one,
  // "Saved in sizes J and K. Worth checking which one they’d like."
  function paintSizeNotes(mode) {
    if (!pageEls.grid) return;
    var groups = {};
    $$('.wishlist-cell[data-size-short]', pageEls.grid).forEach(function (cell) {
      var h = cell.getAttribute('data-handle');
      (groups[h] = groups[h] || []).push(cell);
    });
    Object.keys(groups).forEach(function (h) {
      var cells = groups[h];
      // In the piece's own size order (J before K, £20 before £50), whatever order they were saved in.
      var sizedCells = cells.filter(function (c) { return c.getAttribute('data-size-short'); })
        .sort(function (a, b) { return +a.getAttribute('data-size-index') - +b.getAttribute('data-size-index'); });
      var shorts = sizedCells.map(function (c) { return c.getAttribute('data-size-short'); });
      var kind = cells[0].getAttribute('data-size-kind');
      var text = '';
      if (shorts.length > 1) {
        if (mode === 'shared') text = (kind === 'voucher' ? 'Saved as ' + listWords(shorts) + ' vouchers.' : 'Saved in sizes ' + listWords(shorts) + '.') + ' Worth checking which one they’d like.';
        else text = 'Saved in ' + numberWord(shorts.length) + (kind === 'voucher' ? ' amounts' : ' sizes');
      }
      cells.forEach(function (c) {
        var note = $('[data-wishlist-size-note]', c);
        if (!note) return;
        var show = !!text && !!c.getAttribute('data-size-short');
        if (note.hidden === show) note.hidden = !show;
        var t = show ? text : '';
        if (note.textContent !== t) note.textContent = t;
      });
    });
  }

  // Newest first, but the sizes of one piece sit side by side (at the newest one's place).
  function groupBySize(ordered) {
    var groups = {}, out = [];
    ordered.forEach(function (it) { if (!groups[it.handle]) { groups[it.handle] = []; out.push(groups[it.handle]); } groups[it.handle].push(it); });
    return [].concat.apply([], out);
  }

  function decorateCard(card, item, info, mode, template) {
    var wrapper = info.node;
    var productCard = $('.product-card', wrapper) || wrapper;
    var row = template ? template.content.firstElementChild.cloneNode(true) : document.createElement('div');
    row.className = 'wishlist-card__actions';
    var variant = null, firstAvailable = null;
    info.variants.forEach(function (v) { if (String(v.id) === String(item.variantId)) variant = v; if (!firstAvailable && v.available) firstAvailable = v; });
    var list = pageVariants(info), kind = sizeKindOf(info.optionName, list), sized = list.length > 1;
    var sizeEl = document.createElement('div');
    sizeEl.className = 'wishlist-card__size';
    var sizeName = variant && variant.title && variant.title !== 'Default Title' ? sizeLabel(kind, info.optionName, variant.title) : '';
    card._ntf = { item: item, info: info, mode: mode, template: template };
    card.setAttribute('data-variant-id', item.variantId || '');
    card.setAttribute('data-size-kind', kind);
    card.setAttribute('data-size-short', variant && sized ? wordsFor(kind, variant.title).short : '');
    card.setAttribute('data-size-index', variant ? String(list.map(function (v) { return v.id; }).indexOf(String(variant.id))) : '');
    var primary = document.createElement(mode === 'shared' ? 'button' : 'button');
    primary.type = 'button';
    primary.className = 'btn btn--primary btn--sm';
    var secondary;

    if (!info.available) {
      sizeEl.textContent = sizeName;
      primary.textContent = 'Sold out';
      primary.disabled = true;
    } else if (variant && !variant.available) {
      sizeEl.textContent = sizeName + ' sold out';
      primary = document.createElement('a');
      primary.className = 'btn btn--primary btn--sm';
      primary.href = info.url;
      primary.textContent = 'Choose another ' + (kind === 'voucher' ? 'amount' : 'size');
    } else if (!variant && sized) {
      // Saved without a size (before #305, or from an old shared link): choose one here, in place.
      primary = sizeSelect(card, item, info, list, kind);
    } else {
      sizeEl.textContent = sizeName;
      var buyId = variant ? variant.id : (firstAvailable ? firstAvailable.id : null);
      primary.textContent = mode === 'shared' ? 'Buy this' : 'Add to bag';
      if (buyId) { primary.addEventListener('click', function () { addToBag(buyId, primary); }); card.setAttribute('data-buy-id', buyId); }
      else { primary.disabled = true; primary.textContent = 'Sold out'; }
    }

    if (mode === 'shared') {
      secondary = document.createElement('button');
      secondary.type = 'button';
      secondary.className = 'wishlist-heart wishlist-heart--inline';
      secondary.setAttribute('data-wishlist-heart', '');
      secondary.setAttribute('data-handle', item.handle);
      if (variant) secondary.setAttribute('data-variant-id', item.variantId);
      if (sized) {
        secondary.setAttribute('data-sized', 'true');
        secondary.setAttribute('data-option-name', info.optionName);
        secondary.setAttribute('data-variants', JSON.stringify(list.map(function (v) { return [v.id, v.title, v.available]; })));
      }
      secondary.setAttribute('data-title', info.title);
      secondary.setAttribute('aria-pressed', 'false');
      secondary.setAttribute('data-label-off', 'Save to my wishlist'); // #309: says whose list it goes to
      secondary.innerHTML = '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg><span data-wishlist-heart-label>Save to my wishlist</span>';
    } else {
      secondary = document.createElement('button');
      secondary.type = 'button';
      secondary.className = 'btn btn--ghost btn--sm';
      secondary.textContent = 'Remove';
      secondary.addEventListener('click', function () {
        remove(item.handle, item.variantId);
        card.remove();
        updateSummary(mode, getItems().length);
        paintSizeNotes(mode);
        setStatus(info.title + (variant && sized ? ', ' + wordsFor(kind, variant.title).phrase + ',' : '') + ' removed.');
      });
    }

    row.innerHTML = '';
    if (sizeEl.textContent) row.appendChild(sizeEl);
    var noteEl = document.createElement('div');
    noteEl.className = 'wishlist-card__note';
    noteEl.setAttribute('data-wishlist-size-note', '');
    noteEl.hidden = true;
    row.appendChild(noteEl);
    row.appendChild(primary);
    row.appendChild(secondary);
    productCard.appendChild(row);
    card.appendChild(wrapper);
    renderHearts(card);
  }

  var pageEls = {};
  function updateSummary(mode, count) {
    view.pieces = count;
    if (pageEls.empty) pageEls.empty.hidden = count !== 0;
    if (pageEls.grid) pageEls.grid.hidden = count === 0;
    renderBar();
  }

  function renderList(items, mode) {
    var grid = pageEls.grid;
    grid.innerHTML = '';
    updateSummary(mode, items.length);
    if (!items.length) { setStatus(''); return Promise.resolve(); }
    setStatus('Loading your pieces…');
    var template = $('template[data-wishlist-actions]');
    var dropped = 0, ordered = groupBySize(items.slice().reverse()); // newest first on the page
    var cells = ordered.map(function (it) { var cell = document.createElement('div'); cell.className = 'wishlist-cell'; cell.setAttribute('data-handle', it.handle); grid.appendChild(cell); return cell; });
    // Two sizes of one piece share one fetch; each card gets its own copy of the markup.
    var fetched = {};
    return inBatches(ordered, 6, function (it) { return fetched[it.handle] || (fetched[it.handle] = fetchCard(it.handle)); }).then(function (results) {
      var used = {};
      results.forEach(function (info, i) {
        var cell = cells[i], item = ordered[i];
        if (info.status === 200) {
          // Copies come from untouched markup, taken before the first card is decorated.
          if (!used[item.handle]) used[item.handle] = info.node.cloneNode(true);
          else { var copy = {}; for (var k in info) copy[k] = info[k]; copy.node = used[item.handle].cloneNode(true); info = copy; }
          decorateCard(cell, item, info, mode, template);
          return;
        }
        cell.remove();
        if (info.status === 404) {
          dropped++;
          if (mode !== 'shared') { var st = read(); st.items = st.items.filter(function (x) { return x.handle !== item.handle; }); write(st); }
        } else {
          var note = document.createElement('div');
          note.className = 'wishlist-cell wishlist-cell--error';
          note.textContent = 'Could not load ' + item.handle.replace(/-/g, ' ') + '. Reload to try again.';
          grid.appendChild(note);
        }
      });
      if (dropped && mode !== 'shared') { renderCount(); scheduleSync(false); }
      var remaining = mode === 'shared' ? items.length - dropped : getItems().length;
      updateSummary(mode, remaining);
      paintSizeNotes(mode);
      if (pageEls.dropped) {
        pageEls.dropped.hidden = !dropped;
        pageEls.dropped.textContent = dropped === 1 ? '1 piece is no longer on sale and has been removed.' : dropped + ' pieces are no longer on sale and have been removed.';
      }
      setStatus('');
    });
  }

  function parseShared() {
    var params = new URLSearchParams(location.search);
    var l = params.get('l'), w = params.get('w');
    if (l && /^[a-z0-9]{8}$/.test(l)) return { kind: 'live', id: l };
    if (w) {
      try {
        var data = JSON.parse(b64urlDecode(w));
        var items = (data.i || []).map(function (pair) { return { handle: pair[0], variantId: pair[1] == null ? null : String(pair[1]) }; }).filter(function (it) { return /^[a-z0-9-]{1,255}$/.test(it.handle || ''); });
        return { kind: 'snapshot', ref: w, name: cleanName(data.n) || null, items: items.slice(0, URL_LINK_MAX), truncated: (data.i || []).length > URL_LINK_MAX };
      } catch (e) { return { kind: 'broken' }; }
    }
    return null;
  }

  function renderSharedHeader(title) {
    view.name = 'shared';
    setHeader('Shared with you', title);
    renderBar();
  }

  function initPage() {
    var page = $('[data-wishlist-page]');
    if (!page) return;
    pageEls = {
      grid: $('[data-wishlist-grid]', page), count: $('[data-wishlist-count]', page), empty: $('[data-wishlist-empty]', page),
      dropped: $('[data-wishlist-dropped]', page), signin: $('[data-wishlist-signin]', page), share: $('[data-wishlist-share]', page),
      eyebrow: $('[data-wishlist-eyebrow]', page), title: $('[data-wishlist-title]', page), shareOpen: $('[data-wishlist-share-open]', page),
      addAll: $('[data-wishlist-add-all]', page), unavailable: $('[data-wishlist-unavailable]', page),
      ownLink: $('[data-wishlist-view-link="own"]', page), receivedLink: $('[data-wishlist-view-link="received"]', page),
      ownCount: $('[data-wishlist-view-count="own"]', page), receivedCount: $('[data-wishlist-view-count="received"]', page)
    };
    view.ownTitle = pageEls.title ? pageEls.title.textContent : 'Wishlist';
    statusEl = $('[data-wishlist-status]', page);
    if (pageEls.addAll) pageEls.addAll.addEventListener('click', function () { addAllToBag(pageEls.addAll); });
    initReceivedSync();
    // Hearts on a shared list save to your own list, and another tab can change either list: keep the counts honest.
    document.addEventListener('ntf:wishlist:change', renderBar);
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) renderBar();
      if (e.key !== RECEIVED_KEY) return;
      receivedMem = null;
      renderBar();
      if (view.name === 'received') renderReceived();
    });

    var shared = parseShared();
    if (shared) {
      if (pageEls.empty) pageEls.empty.hidden = true;
      var show = function (title, items, truncated) {
        renderSharedHeader(title);
        renderList(items, 'shared').then(function () {
          if (truncated && pageEls.dropped) { pageEls.dropped.hidden = false; pageEls.dropped.textContent = 'Link carries the first ' + URL_LINK_MAX + ' pieces.'; }
        });
      };
      // The title names the sender; a list without a name goes by the day it was first opened here.
      var titleFor = function (kind, ref, name) { var e = findReceived(kind, ref); return receivedTitle({ name: name, firstOpenedAt: e ? e.firstOpenedAt : nowIso() }); };
      var unavailable = function (temporary) {
        view.unavailable = true;
        renderSharedHeader(temporary ? 'This list couldn’t be loaded' : 'This list is no longer available');
        var why = $('[data-wishlist-unavailable-text]', page);
        if (why && temporary) why.textContent = 'Please check your connection and reload the page.';
        if (pageEls.unavailable) pageEls.unavailable.hidden = false;
        if (pageEls.grid) pageEls.grid.hidden = true;
      };
      if (shared.kind === 'live') {
        renderSharedHeader(view.ownTitle); // until the sender's name arrives
        setStatus('Loading…');
        request('GET', PROXY + '/share/' + shared.id).then(function (res) {
          if (res.ok && res.body && Array.isArray(res.body.items)) {
            var name = cleanName(res.body.name) || null;
            rememberShared('live', shared.id, name, res.body.items.length, !!res.body.mine);
            show(titleFor('live', shared.id, name), res.body.items, false);
          } else if (res.status === 404) {
            markReceivedUnavailable('live', shared.id);
            unavailable(false);
          } else {
            unavailable(true);
          }
        });
      } else if (shared.kind === 'snapshot') {
        rememberShared('snapshot', shared.ref, shared.name, shared.items.length, false);
        show(titleFor('snapshot', shared.ref, shared.name), shared.items, shared.truncated);
      } else {
        unavailable();
      }
      return;
    }

    bindShare(page);
    initViews(page);
    renderList(getItems(), 'own');
    document.addEventListener('ntf:wishlist:change', function () {
      // Another tab or the header heart changed the list: keep the count honest without re-rendering everything.
      updateSummary('own', getItems().length);
    });
  }

  /* ------- Boot ------- */
  function boot() {
    read();
    bindHearts();
    renderHearts();
    renderCount();
    initAccountSync();
    initPage();
    if (CUSTOMER_ID && !read().syncedAt && !$('[data-wishlist-server]')) scheduleSync(false);
    republishNow(); // catches a change made on a page that was left before its update went out
  }

  var origAfter = afterChange;
  afterChange = function () { origAfter(); document.dispatchEvent(new CustomEvent('ntf:wishlist:change', { detail: { count: read().items.length } })); };

  window.NTFWishlist = { getItems: getItems, has: has, add: add, remove: remove, toggle: function (h, v) { if (has(h)) { remove(h); return false; } add(h, v); return true; } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
